'use client';

/**
 * In-app confirm and prompt dialogs.
 *
 * These replace `window.confirm` / `window.prompt` on the canvas. Three
 * reasons they had to go, in order of how much they hurt:
 *
 *  - The native dialogs are **suppressed inside the Fullscreen API** in
 *    Chrome: pressing Delete on a fullscreen diagram either did nothing
 *    visible or silently kicked you out of fullscreen. Full Screen is a
 *    first-class button on this canvas, so a confirmation that cannot be seen
 *    there is a confirmation that does not exist.
 *  - They are synchronous and block the whole tab, so nothing can animate or
 *    show progress behind them.
 *  - They are OS-chrome: unstyled, unthemeable, and they ignore the light /
 *    dark preference entirely.
 *
 * The API is promise-based rather than a hook, because every call site is
 * already inside an `async` mutation handler:
 *
 *     if (!(await confirmDialog({ title: 'Delete "API Gateway"?' }))) return;
 *
 * `<DialogHost />` (mounted once in Providers) renders whatever is pending.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FloatingWindow from '@/components/FloatingWindow';

interface ConfirmOptions {
  title: string;
  /** One line on what actually happens. Optional, but usually worth it. */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for anything that destroys work. */
  danger?: boolean;
}

interface PromptOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  /** A textarea instead of an input -- for instructions rather than names. */
  multiline?: boolean;
  /** Allow submitting an empty string (a connection label, say). */
  allowEmpty?: boolean;
}

export interface ChoiceOption {
  /** Returned by chooseDialog when this option is taken. */
  id: string;
  label: string;
  /** What taking this option actually does. Shown under the label. */
  description?: string;
  icon?: string;
  /** Rendered as the primary option -- one per dialog at most. */
  primary?: boolean;
}

interface ChooseOptions {
  title: string;
  message?: string;
  options: ChoiceOption[];
  cancelLabel?: string;
}

type Request =
  | { id: number; kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { id: number; kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void }
  | { id: number; kind: 'choose'; opts: ChooseOptions; resolve: (v: string | null) => void };

let nextId = 0;

/* Set by DialogHost on mount. A module-level slot rather than context so the
   helpers below stay plain functions callable from any async code. */
let push: ((req: Request) => void) | null = null;

/** Resolves true if confirmed, false if dismissed. Never rejects. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  if (!push) return Promise.resolve(false);
  return new Promise((resolve) => push!({ id: nextId++, kind: 'confirm', opts, resolve }));
}

/** Resolves the entered text, or null if dismissed. Never rejects. */
export function promptDialog(opts: PromptOptions): Promise<string | null> {
  if (!push) return Promise.resolve(null);
  return new Promise((resolve) => push!({ id: nextId++, kind: 'prompt', opts, resolve }));
}

/**
 * A branch, not a yes/no: resolves the chosen option's id, or null if
 * dismissed. Never rejects.
 *
 * Both branches are real work the user asked for, so neither is "confirm"
 * and neither is "cancel" -- shoehorning that into confirmDialog would make
 * one of them the button you press to *not* do the thing.
 */
export function chooseDialog(opts: ChooseOptions): Promise<string | null> {
  if (!push) return Promise.resolve(null);
  return new Promise((resolve) => push!({ id: nextId++, kind: 'choose', opts, resolve }));
}

function Shell({
  title,
  message,
  children,
  onDismiss,
}: {
  title: string;
  message?: string;
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    /* Movable, but `autoHeight` -- so no resize grips. A three-line "Delete
       this?" has nothing to resize, and a drag handle is still worth having:
       the confirmation names a service you may want to look at on the diagram
       underneath before answering.
       z-[80] puts it above BusyOverlay (70), itself above every panel. */
    <FloatingWindow
      id="dialog"
      title={title}
      width={440}
      /* Not a rendered height (autoHeight owns that) -- only the figure the
         opening position is centred against. */
      height={220}
      minWidth={300}
      autoHeight
      zIndex={80}
      /* Dismissing by clicking away is too easy to do by accident on a
         destructive confirmation; Cancel and Escape are the ways out. */
      closeOnScrim={false}
      onClose={onDismiss}
    >
      <div className="p-5 pt-4">
        {/* whitespace-pre-line so a caller can list what it is about to do, one
            item per line, instead of running it all into one paragraph. */}
        {message && (
          <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
            {message}
          </p>
        )}
        {children}
      </div>
    </FloatingWindow>
  );
}

function ConfirmBody({ opts, resolve }: { opts: ConfirmOptions; resolve: (v: boolean) => void }) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => confirmRef.current?.focus(), []);

  return (
    <Shell title={opts.title} message={opts.message} onDismiss={() => resolve(false)}>
      <div className="flex items-center justify-end gap-2 mt-5">
        <button onClick={() => resolve(false)} className="btn-secondary text-xs px-3 py-1.5">
          {opts.cancelLabel ?? 'Cancel'}
        </button>
        <button
          ref={confirmRef}
          onClick={() => resolve(true)}
          className={`${opts.danger ? 'btn-danger' : 'btn-primary'} text-xs px-3 py-1.5 flex items-center gap-1.5`}
        >
          {opts.danger && <Icon name="TrashIcon" size={12} />}
          {opts.confirmLabel ?? (opts.danger ? 'Delete' : 'Confirm')}
        </button>
      </div>
    </Shell>
  );
}

function PromptBody({
  opts,
  resolve,
}: {
  opts: PromptOptions;
  resolve: (v: string | null) => void;
}) {
  const [value, setValue] = useState(opts.defaultValue ?? '');
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select?.();
  }, []);

  const submit = useCallback(() => {
    if (!opts.allowEmpty && !value.trim()) return;
    resolve(value);
  }, [opts.allowEmpty, resolve, value]);

  const fieldClass = 'input-dark w-full mt-3';
  const disabled = !opts.allowEmpty && !value.trim();

  return (
    <Shell title={opts.title} message={opts.message} onDismiss={() => resolve(null)}>
      {opts.multiline ? (
        <textarea
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={opts.placeholder}
          rows={3}
          className={`${fieldClass} resize-y`}
          /* Enter submits, shift-Enter breaks the line -- the same contract as
             the chat inputs elsewhere in the app. */
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
      ) : (
        <input
          ref={ref as React.RefObject<HTMLInputElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={opts.placeholder}
          className={fieldClass}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <button onClick={() => resolve(null)} className="btn-secondary text-xs px-3 py-1.5">
          Cancel
        </button>
        <button onClick={submit} disabled={disabled} className="btn-primary text-xs px-3 py-1.5">
          {opts.confirmLabel ?? 'OK'}
        </button>
      </div>
    </Shell>
  );
}

function ChooseBody({
  opts,
  resolve,
}: {
  opts: ChooseOptions;
  resolve: (v: string | null) => void;
}) {
  const firstRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => firstRef.current?.focus(), []);

  return (
    <Shell title={opts.title} message={opts.message} onDismiss={() => resolve(null)}>
      {/* Stacked cards rather than a row of buttons: each option carries a
          line explaining what it does to the diagram's history, and that
          does not fit on a button face. */}
      <div className="flex flex-col gap-2 mt-4">
        {opts.options.map((opt, i) => (
          <button
            key={opt.id}
            ref={i === 0 ? firstRef : undefined}
            onClick={() => resolve(opt.id)}
            className={`surface-card surface-card-hover w-full text-left p-3 flex items-start gap-3 ${
              opt.primary ? 'border-primary/60' : ''
            }`}
          >
            {opt.icon && (
              <span
                className="tile-icon w-8 h-8 mt-0.5"
                style={
                  opt.primary
                    ? undefined
                    : { ['--tile-accent' as string]: 'var(--muted-foreground)' }
                }
              >
                <Icon name={opt.icon} size={15} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold">{opt.label}</span>
              {opt.description && (
                <span className="block text-2xs text-muted-foreground leading-relaxed mt-0.5">
                  {opt.description}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-end mt-4">
        <button onClick={() => resolve(null)} className="btn-secondary text-xs px-3 py-1.5">
          {opts.cancelLabel ?? 'Cancel'}
        </button>
      </div>
    </Shell>
  );
}

/** Mounted once, at the root. Renders the oldest pending request. */
export default function DialogHost() {
  const [queue, setQueue] = useState<Request[]>([]);

  useEffect(() => {
    push = (req) => setQueue((q) => [...q, req]);
    return () => {
      push = null;
    };
  }, []);

  const current = queue[0];
  const settle = useCallback((req: Request, value: unknown) => {
    setQueue((q) => q.filter((r) => r !== req));
    (req.resolve as (v: unknown) => void)(value);
  }, []);

  if (!current) return null;

  /* Keyed by request id so the next dialog in the queue mounts fresh --
     otherwise a prompt would inherit the previous one's typed value. */
  if (current.kind === 'confirm') {
    return <ConfirmBody key={current.id} opts={current.opts} resolve={(v) => settle(current, v)} />;
  }
  if (current.kind === 'choose') {
    return <ChooseBody key={current.id} opts={current.opts} resolve={(v) => settle(current, v)} />;
  }
  return <PromptBody key={current.id} opts={current.opts} resolve={(v) => settle(current, v)} />;
}
